/**
 * FlightTimeline 工具函数
 * 抽取自 FlightTimeline.jsx，便于单元测试。
 */
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const BJ_TZ = "Asia/Shanghai";

/**
 * 返回航班当前最可信的起飞/到达时间。
 *
 * 时间优先级统一为：实际 > 预计 > 计划。这样执行中的航班可以使用
 * ATD + ETA，未来航班使用 ETD + ETA，只有没有动态时间时才回退到
 * STD + STA。
 */
export const getEffectiveDepartureTime = (flight) =>
    flight?.atd || flight?.etd || flight?.std || null;

export const getEffectiveArrivalTime = (flight) =>
    flight?.ata || flight?.eta || flight?.sta || null;

/**
 * 将"日期型"输入归一化成北京时区下的 YYYY-MM-DD。
 *
 * 后端 flight_operation.flight_date 是 UTC 时间戳，rotation 含义。
 * 维护事件录入存的是 YYYY-MM-DD 字符串。为避免浏览器本地时区漂移，
 * 这里统一在北京时区下取日期。
 */
export const toBjDateStr = (input) => {
    if (!input) return null;
    return dayjs(input).tz(BJ_TZ).format("YYYY-MM-DD");
};

/**
 * 在飞机航班列表中精确匹配锚点航班，返回起飞或落地时间。
 *
 * 核心契约：
 * 1. 必须传入 targetDate（维护事件的 prev/next_flight_date），且只做"按北京时区
 *    YYYY-MM-DD 严格相等"的匹配。
 * 2. 若没有任何航班的 flight_date 与 targetDate 相等，**返回 null**，
 *    由调用方走 fallback 路径（用维护事件日期 clamp 到窗口边界）。
 *    —— 旧实现"按日期接近度回退"会在锚点航班不在 3 天窗口内时，
 *       错误地选到同航班号的相邻日期航班，导致 startTime > endTime，
 *       宽度计算为负、维护条整体不渲染。
 * 3. 同一航班号同一日期可能有多个航段（如经停）：
 *    - timeType="sta"（前一航班的落地） → 取最后一个航段
 *    - timeType="std"（后一航班的起飞） → 取第一个航段
 *
 * @param {Array<Object>} aircraftFlights 飞机的航班数组
 * @param {string} flightNumber 锚点航班号
 * @param {string} flightDate 锚点航班日期（YYYY-MM-DD 或 ISO 字符串）
 * @param {"sta"|"std"} timeType 取落地时间还是起飞时间
 * @returns {string|null} ISO 时间字符串，匹配不到返回 null
 */
export const findFlightTime = (aircraftFlights, flightNumber, flightDate, timeType) => {
    if (!aircraftFlights || !flightNumber) return null;

    const targetDate = toBjDateStr(flightDate);
    if (!targetDate) return null;

    const candidates = aircraftFlights.filter((f) => {
        if (f.flight_number !== flightNumber) return false;
        const fDate = toBjDateStr(f.flight_date || f.std || f.etd);
        return fDate === targetDate;
    });

    if (candidates.length === 0) return null;

    const sorted = [...candidates].sort((a, b) =>
        dayjs(getEffectiveDepartureTime(a)).diff(dayjs(getEffectiveDepartureTime(b)))
    );

    const flight = timeType === "sta" ? sorted[sorted.length - 1] : sorted[0];
    return timeType === "sta"
        ? getEffectiveArrivalTime(flight)
        : getEffectiveDepartureTime(flight);
};
